import {
  BUFF_STACK_BONUS_PCT,
  BULLET_LIFETIME,
  BULLET_MAX_BOUNCES,
  BULLET_RADIUS,
  BULLET_SPEED,
  CELL,
  EXPLOSION_RADIUS,
  FIRE_COOLDOWN,
  LASER_DELAY,
  LASER_RANGE,
  MAX_AMMO,
  MULTISHOT_COUNT,
  MULTISHOT_SPREAD_DEG,
  RAPIDFIRE_COUNT,
  RAPIDFIRE_DELAY,
  SCOPE_SECONDS,
  RELOAD_SECONDS,
  SHIELD_SECONDS,
  SNIPER_SPEED_MULT,
  SNIPER_WALL_PIERCE,
  SPEED_BOOST_MULT,
  SPEED_BOOST_SECONDS,
  TANK_ACCEL,
  TANK_DECEL,
  TANK_RADIUS,
  TANK_TURN_SPEED,
  TRACKING_BOUNCES,
  TRACKING_LIFETIME,
  TRACKING_TURN_RATE,
  WALL_HP,
  WALL_THICKNESS,
} from "../constants.js";

export const FOG_VISION_MODES = ["off", "team", "all"] as const;
export type FogVisionMode = (typeof FOG_VISION_MODES)[number];

export const HAZARD_TYPES = ["lava", "mud", "ice", "heal"] as const;
export type HazardType = (typeof HAZARD_TYPES)[number];

/**
 * Advanced, normally-hardcoded tuning values, exposed so a host can override
 * every object's behaviour. Defaults mirror the engine constants.
 */
export interface AdvancedConfig {
  tankRadius: number;
  tankTurnSpeed: number; // rad/s
  tankAccel: number; // px/s² while a movement key is held (momentum wind-up)
  tankDecel: number; // px/s² while no movement input (momentum slow-down)
  fireCooldown: number; // s between shots
  maxAmmo: number;
  reloadSeconds: number;
  bulletSpeed: number; // px/s
  bulletRadius: number;
  bulletBounces: number;
  bulletLifetime: number; // s
  cellSize: number; // px per maze cell
  wallThickness: number; // px
  wallHp: number; // hits to destroy an internal wall (destructibleWalls only)
  speedBoostMult: number;
  speedBoostSeconds: number;
  shieldSeconds: number;
  buffStackBonusPct: number; // % extra duration when a speed/shield pickup stacks onto an active buff
  laserDelay: number; // s windup
  sniperSpeedMult: number;
  sniperWallPierce: number; // walls a sniper round punches through (0 = none)
  explosionRadius: number;
  scopeSeconds: number; // duration of the line-of-sight scope buff
  trackingTurnRate: number; // rad/s
  trackingLifetime: number; // s a tracking round lives (governs its range)
  trackingBounces: number; // wall bounces a tracking round survives
  multishotCount: number; // pellets released per multishot
  multishotSpread: number; // total fan angle (degrees) of a multishot
  laserRange: number; // px total beam length
  rapidFireCount: number; // bullets fired per rapid-fire burst
  rapidFireDelay: number; // s between each rapid-fire burst shot
}

export const DEFAULT_ADVANCED: AdvancedConfig = {
  tankRadius: TANK_RADIUS,
  tankTurnSpeed: TANK_TURN_SPEED,
  tankAccel: TANK_ACCEL,
  tankDecel: TANK_DECEL,
  fireCooldown: FIRE_COOLDOWN,
  maxAmmo: MAX_AMMO,
  reloadSeconds: RELOAD_SECONDS,
  bulletSpeed: BULLET_SPEED,
  bulletRadius: BULLET_RADIUS,
  bulletBounces: BULLET_MAX_BOUNCES,
  bulletLifetime: BULLET_LIFETIME,
  cellSize: CELL,
  wallThickness: WALL_THICKNESS,
  wallHp: WALL_HP,
  speedBoostMult: SPEED_BOOST_MULT,
  speedBoostSeconds: SPEED_BOOST_SECONDS,
  shieldSeconds: SHIELD_SECONDS,
  buffStackBonusPct: BUFF_STACK_BONUS_PCT,
  laserDelay: LASER_DELAY,
  sniperSpeedMult: SNIPER_SPEED_MULT,
  sniperWallPierce: SNIPER_WALL_PIERCE,
  explosionRadius: EXPLOSION_RADIUS,
  scopeSeconds: SCOPE_SECONDS,
  trackingTurnRate: TRACKING_TURN_RATE,
  trackingLifetime: TRACKING_LIFETIME,
  trackingBounces: TRACKING_BOUNCES,
  multishotCount: MULTISHOT_COUNT,
  multishotSpread: MULTISHOT_SPREAD_DEG,
  laserRange: LASER_RANGE,
  rapidFireCount: RAPIDFIRE_COUNT,
  rapidFireDelay: RAPIDFIRE_DELAY,
};
